import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const GeographicHeatMap = ({ data, totalDownloads }) => {
  // Process data to calculate percentages and create heat map representation
  const processedData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    const total = totalDownloads || data?.reduce((sum, item) => sum + item?.count, 0);
    
    return data?.map(item => ({
        ...item,
        percentage: total > 0 ? ((item?.count / total) * 100) : 0,
        intensity: item?.count // Used for color intensity
      }))?.sort((a, b) => b?.count - a?.count)?.slice(0, 10); // Show top 10 countries
  }, [data, totalDownloads]);

  // Get color intensity based on download count
  const getColorIntensity = (count, maxCount) => {
    const intensity = maxCount > 0 ? (count / maxCount) : 0;
    if (intensity >= 0.8) return 'bg-blue-600';
    if (intensity >= 0.6) return 'bg-blue-500';
    if (intensity >= 0.4) return 'bg-blue-400';
    if (intensity >= 0.2) return 'bg-blue-300';
    return 'bg-blue-200';
  };

  // Country flag emojis (simplified representation)
  const countryFlags = {
    'US': '🇺🇸',
    'UK': '🇬🇧',
    'Canada': '🇨🇦',
    'Australia': '🇦🇺',
    'Germany': '🇩🇪',
    'France': '🇫🇷',
    'Japan': '🇯🇵',
    'Brazil': '🇧🇷',
    'India': '🇮🇳',
    'Mexico': '🇲🇽'
  };

  const maxCount = Math.max(...(processedData?.map(item => item?.count) || [1]));

  if (!processedData?.length) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-text-primary mb-2">Geographic Distribution</h3>
          <p className="text-sm text-text-secondary">Download locations worldwide</p>
        </div>
        
        <div className="flex items-center justify-center h-80">
          <div className="text-center">
            <Icon name="Globe" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No geographic data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-text-primary mb-2">Geographic Distribution</h3>
        <p className="text-sm text-text-secondary">Download locations worldwide</p>
      </div>
      {/* World Heat Map Representation */}
      <div className="mb-6">
        <div className="grid grid-cols-1 gap-3">
          {processedData?.map((country, index) => (
            <div 
              key={country?.country}
              className="flex items-center justify-between p-3 rounded-lg bg-background/50 hover:bg-background transition-colors duration-200"
            >
              <div className="flex items-center space-x-3">
                <span className="text-lg">
                  {countryFlags?.[country?.country] || '🌍'}
                </span>
                <div>
                  <div className="font-medium text-text-primary">
                    {country?.country}
                  </div>
                  <div className="text-sm text-text-secondary">
                    Rank #{index + 1}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* Visual bar representation */}
                <div className="w-24 h-3 bg-border rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${getColorIntensity(country?.count, maxCount)}`}
                    style={{ width: `${Math.max(10, (country?.count / maxCount) * 100)}%` }}
                  />
                </div>
                
                <div className="text-right min-w-[80px]">
                  <div className="font-semibold text-text-primary">
                    {country?.count}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {country?.percentage?.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Summary Stats */}
      <div className="border-t border-border pt-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-text-primary">
              {processedData?.length}
            </div>
            <div className="text-xs text-text-secondary">Countries</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-text-primary">
              {processedData?.[0]?.country || 'N/A'}
            </div>
            <div className="text-xs text-text-secondary">Top Country</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-text-primary">
              {processedData?.[0]?.percentage?.toFixed(1) || 0}%
            </div>
            <div className="text-xs text-text-secondary">Top Share</div>
          </div>
        </div>
      </div>
      {/* Color Legend */}
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-text-secondary">
          <span>Less</span>
          <div className="flex space-x-1">
            {['bg-blue-200', 'bg-blue-300', 'bg-blue-400', 'bg-blue-500', 'bg-blue-600']?.map((color, index) => (
              <div key={index} className={`w-4 h-3 rounded-sm ${color}`} />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>
    </div>
  );
};

export default GeographicHeatMap;